import {ChangeDetectionStrategy, Component, Input} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import {map, takeUntil} from "rxjs/operators";
import {DestroyStreamService} from "../../services";

@Component({
  selector: 'int-ip-address',
  templateUrl: './int-ip-address.component.html',
  styleUrls: ['./int-ip-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: IntIpAddressComponent,
      multi: true
    }
  ]
})
export class IntIpAddressComponent implements ControlValueAccessor {

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() disabled = false;
  @Input() validatorFn: (value: string) => boolean = () => true;

  @Input() divider: string = ',';

  list$: BehaviorSubject<Array<string> | null> = new BehaviorSubject<Array<string> | null>([]);
  focus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  value$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  showAddAction$ = this.value$
    .pipe(
      map((value) => {
        if(this.isDividerPresent(value)) {
          return value
            .split(this.divider)
            .map(v => (v || '').trim())
            .filter(v => !!v)
            .every(v => this.validatorFn(v));
        }
        return this.validatorFn(value)
      }),
      takeUntil(this.destroy$)
    );

  constructor(
    private readonly destroy$: DestroyStreamService
  ) {
  }

  get outputLabel() {
    return this.isOpen ? (this.label || this.placeholder) : this.placeholder;
  }

  get data() {
    return (this.list$.value || []);
  }

  get isActive() {
    return this.data.length > 0 || this.isOpen || (this.currentValue || '').length > 0;
  }

  get isOpen() {
    return this.focus$.value;
  }

  get isError() {
    return false;
  }

  get currentValue() {
    return this.value$.value;
  }

  isDividerPresent(value: string) {
    return value.includes(this.divider);
  }

  onClickHandler(element: HTMLInputElement) {
    element.focus();
  }

  setValue(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.value$.next(value);
  }

  setFocus(status: boolean) {
    this.focus$.next(status);
  }

  delete(record: string) {
    if(this.disabled) {
      return;
    }
    const list = this.data.filter(r => r !== record);
    this.setData(list);
  }

  add() {
    if(this.isDividerPresent(this.currentValue)) {
      const list = this.currentValue
        .split(this.divider)
        .map(v => (v || '').trim())
        .filter(v => !!v)
        .filter(v => this.validatorFn(v));
      this.setData([...this.data, ...list]);
      this.value$.next('');
      return
    }
    this.setData([...this.data, this.currentValue]);
    this.value$.next('');
  }

  setData(list: Array<string>) {
    this.onChange(list);
    this.list$.next(list);
  }

  writeValue(list: Array<string> | null) {
    this.list$.next(list);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  onTouched() {}

  onChange(data: Array<string>) {}
}
